import { Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import { deleteAccount } from "@/redux/reducers/auth";
import { AccountSettingDeleteAccountFieldsSchema } from "@/schemas";
import { authService } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

export const Route = createFileRoute(
	"/dashboard/accounts/settings/account/delete-account",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();
	const dispatch = useDispatch();
	const deleteError = useSelector(
		(state: any) => state.auth?.deleteAccount?.error,
	);

	const { mutate: mutateLogout } = useMutation({
		mutationFn: () => authService.logout(),
		onSuccess: () => {
			queryClient.clear();
			navigate({
				to: "/delete-account-success",
				replace: true,
			});
		},
	});

	const {
		control,
		handleSubmit,
		formState: { isValid, isDirty, isSubmitting },
	} = useForm<AccountSettingDeleteAccountFieldsSchema>({
		mode: "all",
		resolver: zodResolver(AccountSettingDeleteAccountFieldsSchema),
	});

	const onSubmit: SubmitHandler<
		AccountSettingDeleteAccountFieldsSchema
	> = async (_, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		return dispatch(
			deleteAccount({
				callback: () => {
					mutateLogout(undefined, {
						onSuccess: () => {
							return navigate({
								to: "/delete-account-success",
								replace: true,
							});
						},
					});
				},
			}),
		);
	};

	useEffect(() => {
		if (deleteError) {
			toast.error("Failed to delete account");
		}
	}, [deleteError]);

	return (
		<div className="wrapper !p-0 min-h-dvh mx-auto">
			<BackAppBar />
			<form onSubmit={handleSubmit(onSubmit)} className="container">
				<Stack spacing={4}>
					<Typography fontWeight={600} fontSize={28}>
						Delete account
					</Typography>
					<Typography fontWeight={400} fontSize={14}>
						You can permanently delete your account from here. If you’re sure
						about this and you choose to move ahead, all the data connected to
						this account will be deleted permanently. You will not be able to
						retrieve it in the future.
					</Typography>
					<Controller
						control={control}
						name="understand"
						defaultValue={false}
						render={({ field }) => (
							<FormControlLabel
								value="bottom"
								control={<Checkbox {...field} />}
								label="I understand that deleting my account will remove all my data permanently"
								labelPlacement="end"
							/>
						)}
					/>
				</Stack>
				<Box className="fixed bg-white shadow bottom-0 inset-x-0 z-50 py-3">
					<Box className="wrapper mx-auto">
						<Button
							type="submit"
							variant="contained"
							color="error"
							className="w-full !h-12 text-sm font-semibold !shadow-none !rounded-full"
							size="large"
							disabled={!isDirty || !isValid || isSubmitting}
						>
							Delete this account
						</Button>
					</Box>
				</Box>
			</form>
		</div>
	);
}
